/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { IInspectorControl_Comments, TInspectorControlProps } from "../InspectorControlTypes";
import { useUserInfo } from "../../../ui/hooks/useUserInfo";
import { InspectorControlCommentsAdd } from "./InspectorControlCommentsAdd";
import { InspectorControlCommentsList } from "./InspectorControlCommentsList";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";

export interface IInspectorControlCommentsProps extends TInspectorControlProps<IInspectorControl_Comments> {}

export const InspectorControlComments: React.FC<IInspectorControlCommentsProps> = ({ model: _model }) => {
	const { model } = useBlueprintModel(_model, "comments");
	const baseClass = BEM("edt-inspector-control-comments");

	const userInfo = useUserInfo();

	const addMessage = useCallback(
		(value: string) => {
			model.schema.addMessage(
				model,
				{
					message: value,
					authorName: `${userInfo?.firstName} ${userInfo?.lastName}`,
					authorId: userInfo?.accountId,
					date: new Date()
				},
				true
			);
		},
		[ model ]
	);

	const acceptMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.acceptMessage(model, index, true);
		},
		[ model ]
	);

	const declineMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.declineMessage(model, index, true);
		},
		[ model ]
	);

	const removeMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.removeMessage(model, index, true);
		},
		[ model ]
	);

	const unresolveMessage = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.unresolveMessage(model, index, true);
		},
		[ model ]
	);

	return (
		<div className={baseClass.toClassName()} style={{ flex: 1 }}>
			<InspectorControlCommentsAdd model={model} onAdd={addMessage} />

			<InspectorControlCommentsList
				model={model.messages}
				onAcceptMessage={acceptMessage}
				onDeclineMessage={declineMessage}
				onRemoveMessage={removeMessage}
				onUnresolveMessage={unresolveMessage}
			/>
		</div>
	);
};
